'use client';

import { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

import SpinnerLoader from './SpinnerLoader';

type LoadingOverlayProps = {
  size?: number;
  className?: string;
};

// Full-screen loading overlay per DESIGN.md:
// - fixed inset-0, z-50, `bg-background/80` (light) / `bg-background/95` (dark).
// - NO backdrop-blur (reserved for dialogs only).
// - No card wrapper — a single spinner centered on the surface.
// - Traps focus inside so keyboard navigation does not escape to the
//   busy/inert app behind the overlay. The overlay itself is the focus target.
export default function LoadingOverlay({ size = 40, className }: LoadingOverlayProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // Move focus into the overlay on mount, and re-trap if focus tries to leave.
  // Returning focus on unmount is intentionally not attempted — the caller
  // usually replaces the overlay with new content that controls its own focus.
  useEffect(() => {
    const node = overlayRef.current;
    if (node === null) {
      return;
    }

    const previouslyFocused = document.activeElement;
    node.focus();

    const handleFocusIn = (event: FocusEvent) => {
      if (node.contains(event.target as Node) === true) {
        return;
      }
      event.stopPropagation();
      node.focus();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }
      // Nothing focusable inside the overlay; keep focus pinned to the root.
      event.preventDefault();
      node.focus();
    };

    document.addEventListener('focusin', handleFocusIn);
    node.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      node.removeEventListener('keydown', handleKeyDown);
      if (
        previouslyFocused !== null
        && previouslyFocused instanceof HTMLElement
      ) {
        previouslyFocused.focus();
      }
    };
  }, []);

  return (
    <div
      ref={overlayRef}
      tabIndex={-1}
      className={cn(
        'fixed inset-0 z-[var(--z-modal-overlay)] flex items-center justify-center bg-background/80 dark:bg-background/95 outline-none',
      )}
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <SpinnerLoader size={size} className={cn('text-primary', className)} />
    </div>
  );
}
